import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Plus, X, FolderPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const emptyGroup = () => ({ match: 'all', conditions: [] });
const emptyCondition = () => ({ field: '', op: '', value: '' });
const isGroup = (c) => c && typeof c === 'object' && Array.isArray(c.conditions);
const NO_VALUE_OPS = new Set(['is_set', 'is_empty', 'is_true', 'is_false']);

// One-click re-engagement / engagement presets (contact lists only).
const PRESETS = [
  { label: 'Never opened an email', conds: [{ field: 'opened_email', op: 'is_false', value: '' }] },
  { label: 'Opened but no click', conds: [{ field: 'opened_email', op: 'is_true', value: '' }, { field: 'clicked_email', op: 'is_false', value: '' }] },
  { label: 'Clicked a link', conds: [{ field: 'clicked_email', op: 'is_true', value: '' }] },
  { label: 'Replied', conds: [{ field: 'replied_email', op: 'is_true', value: '' }] },
  { label: 'Has an email', conds: [{ field: 'email', op: 'is_set', value: '' }] },
];

// Toggleable badge multi-select used for "has any of" / "has all of" / "is any of".
const MultiSelect = ({ options, value, onChange }) => {
  const vals = Array.isArray(value) ? value : [];
  const toggle = (o) => onChange(vals.includes(o) ? vals.filter(v => v !== o) : [...vals, o]);
  if (!options || options.length === 0) {
    return (
      <Input
        value={vals.join(', ')}
        onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
        placeholder="comma,separated,values"
        className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]"
        data-testid="fb-multi-input"
      />
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5 flex-1 min-w-[160px]" data-testid="fb-multi-badges">
      {options.map(o => (
        <button
          type="button"
          key={o}
          onClick={() => toggle(o)}
          className={`px-2 py-1 rounded-md text-xs border transition-colors ${
            vals.includes(o)
              ? 'bg-violet-600 border-violet-500 text-white'
              : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
};

const ValueEditor = ({ meta, op, value, onChange }) => {
  if (!meta || NO_VALUE_OPS.has(op)) return null;
  const type = meta.type;
  const options = meta.options || [];

  if (type === 'tags') {
    if (op === 'has_any' || op === 'has_all') {
      return <MultiSelect options={options} value={value} onChange={onChange} />;
    }
    // single tag
    if (options.length > 0) {
      return (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]" data-testid="fb-value-select"><SelectValue placeholder="Select tag" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 max-h-64">
            {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="tag" className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]" data-testid="fb-value-input" />;
  }

  if (type === 'list_ref' || type === 'choice') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]" data-testid="fb-value-select"><SelectValue placeholder="Select list" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-64">
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  if (type === 'enum' && op === 'is_any_of') {
    return <MultiSelect options={options} value={value} onChange={onChange} />;
  }
  if (type === 'enum' && options.length > 0) {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]" data-testid="fb-value-select"><SelectValue placeholder="Select value" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-64">
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  const inputType = type === 'number' || op === 'in_last_days' ? 'number' : type === 'date' ? 'date' : 'text';
  return (
    <Input
      type={inputType}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={op === 'in_last_days' ? 'days' : 'value'}
      className="bg-slate-900 border-slate-700 h-9 text-sm flex-1 min-w-[160px]"
      data-testid="fb-value-input"
    />
  );
};

const ConditionRow = ({ fieldsFlat, operators, cond, onChange, onRemove }) => {
  const meta = fieldsFlat.find(f => f.field === cond.field);
  const ops = meta ? (operators[meta.type] || []) : [];

  const setField = (field) => {
    const m = fieldsFlat.find(f => f.field === field);
    const firstOp = m ? (operators[m.type]?.[0]?.[0] || '') : '';
    onChange({ field, op: firstOp, value: '' });
  };
  const setOp = (op) => onChange({ ...cond, op, value: '' });
  const setValue = (value) => onChange({ ...cond, value });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fb-condition-row">
      <Select value={cond.field || ''} onValueChange={setField}>
        <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm w-[190px]" data-testid="fb-field-select"><SelectValue placeholder="Choose field" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
          {fieldsFlat.map(f => (
            <SelectItem key={f.field} value={f.field}>{f.groupLabel}: {f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {meta && (
        <Select value={cond.op || ''} onValueChange={setOp}>
          <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm w-[150px]" data-testid="fb-op-select"><SelectValue placeholder="Operator" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {ops.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <ValueEditor meta={meta} op={cond.op} value={cond.value} onChange={setValue} />

      <button type="button" onClick={onRemove} className="text-slate-500 hover:text-rose-400 p-1" data-testid="fb-remove-condition">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

function Group({ fieldsFlat, operators, group, onChange, onRemove, depth = 0 }) {
  const update = (next) => onChange(next);
  const setMatch = (match) => update({ ...group, match });
  const updateCond = (i, val) => {
    const conditions = group.conditions.slice();
    conditions[i] = val;
    update({ ...group, conditions });
  };
  const removeCond = (i) => update({ ...group, conditions: group.conditions.filter((_, idx) => idx !== i) });
  const addCond = () => update({ ...group, conditions: [...group.conditions, emptyCondition()] });
  const addGroup = () => update({ ...group, conditions: [...group.conditions, emptyGroup()] });

  return (
    <div className={`rounded-lg border border-slate-800 p-3 space-y-2.5 ${depth > 0 ? 'bg-slate-950/60' : 'bg-slate-950'}`} data-testid="fb-group">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">Match</span>
        <Select value={group.match} onValueChange={setMatch}>
          <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs w-[150px]" data-testid="fb-match-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">ALL (AND)</SelectItem>
            <SelectItem value="any">ANY (OR)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">of the following</span>
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-auto text-slate-500 hover:text-rose-400 p-1" data-testid="fb-remove-group">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-2 pl-2 border-l-2 border-slate-800">
        {group.conditions.length === 0 && (
          <p className="text-xs text-slate-600 italic py-1">No conditions yet - add one below.</p>
        )}
        {group.conditions.map((c, i) => isGroup(c) ? (
          <Group.Self key={i} fieldsFlat={fieldsFlat} operators={operators} group={c}
                 onChange={(v) => updateCond(i, v)} onRemove={() => removeCond(i)} depth={depth + 1} />
        ) : (
          <ConditionRow key={i} fieldsFlat={fieldsFlat} operators={operators} cond={c}
                        onChange={(v) => updateCond(i, v)} onRemove={() => removeCond(i)} />
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={addCond}
                className="h-7 text-xs bg-slate-900 border-slate-700 text-slate-300" data-testid="fb-add-condition">
          <Plus className="w-3 h-3 mr-1" /> Add condition
        </Button>
        {depth < 2 && (
          <Button type="button" size="sm" variant="outline" onClick={addGroup}
                  className="h-7 text-xs bg-slate-900 border-slate-700 text-slate-300" data-testid="fb-add-group">
            <FolderPlus className="w-3 h-3 mr-1" /> Add group
          </Button>
        )}
      </div>
    </div>
  );
}
Group.Self = Group;

export const FilterBuilder = ({ objectType, value, onChange }) => {
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    axios.get(`${API}/lists/fields`, { params: { object_type: objectType } })
      .then(r => setCatalog(r.data))
      .catch(() => setCatalog({ groups: [], operators: {} }));
  }, [objectType]);

  const fieldsFlat = useMemo(() => {
    if (!catalog) return [];
    return (catalog.groups || []).flatMap(g =>
      (g.fields || []).map(f => ({ ...f, groupLabel: g.label }))
    );
  }, [catalog]);

  if (!catalog) {
    return <p className="text-xs text-slate-500" data-testid="fb-loading">Loading fields…</p>;
  }

  const group = value || emptyGroup();
  const applyPreset = (conds) => onChange({ ...group, conditions: [...group.conditions, ...conds] });

  return (
    <div data-testid="filter-builder">
      {objectType === 'contact' && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="fb-presets">
          <span className="text-xs text-slate-500 mr-1">Quick add:</span>
          {PRESETS.map(p => (
            <button
              type="button"
              key={p.label}
              onClick={() => applyPreset(p.conds)}
              className="px-2 py-1 rounded-md text-xs bg-slate-900 border border-slate-700 text-slate-300 hover:border-violet-500 hover:text-white transition-colors"
              data-testid={`fb-preset-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              + {p.label}
            </button>
          ))}
        </div>
      )}
      <Group fieldsFlat={fieldsFlat} operators={catalog.operators || {}} group={group} onChange={onChange} />
    </div>
  );
};

export default FilterBuilder;

